// Deterministic visual identity helpers for events, performers, and categories.
// We don't have real imagery, so each item gets a stable gradient + emoji glyph
// derived from its id/category — gives the UI variety without artwork.

export type CategoryStyle = {
  gradient: string;
  glyph: string;
  shortLabel: string;
};

export const CATEGORY_STYLES: Record<string, CategoryStyle> = {
  cat_concerts: {
    gradient: "linear-gradient(135deg, #7c3aed 0%, #ec4899 55%, #f97316 100%)",
    glyph: "♫",
    shortLabel: "Music",
  },
  cat_sports: {
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #22d3ee 50%, #34d399 100%)",
    glyph: "★",
    shortLabel: "Sports",
  },
  cat_theater: {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)",
    glyph: "✦",
    shortLabel: "Stage",
  },
  cat_comedy: {
    gradient: "linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)",
    glyph: "✸",
    shortLabel: "Comedy",
  },
  cat_family: {
    gradient: "linear-gradient(135deg, #06b6d4 0%, #22d3ee 50%, #a3e635 100%)",
    glyph: "❋",
    shortLabel: "Family",
  },
  cat_festivals: {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #d946ef 50%, #f43f5e 100%)",
    glyph: "✺",
    shortLabel: "Festivals",
  },
};

const FALLBACK: CategoryStyle = {
  gradient: "linear-gradient(135deg, #475569 0%, #64748b 50%, #94a3b8 100%)",
  glyph: "♦",
  shortLabel: "Live",
};

export function styleForCategory(categoryId: string): CategoryStyle {
  return CATEGORY_STYLES[categoryId] ?? FALLBACK;
}

// Deterministic palette index from a string id.
function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

const ACCENT_PAIRS: [string, string][] = [
  ["#7c3aed", "#ec4899"],
  ["#0ea5e9", "#22d3ee"],
  ["#f59e0b", "#ef4444"],
  ["#06b6d4", "#a3e635"],
  ["#8b5cf6", "#f43f5e"],
  ["#ec4899", "#f97316"],
  ["#3b82f6", "#8b5cf6"],
  ["#10b981", "#22d3ee"],
];

export function gradientFor(id: string, categoryId?: string): string {
  if (categoryId && CATEGORY_STYLES[categoryId]) {
    return CATEGORY_STYLES[categoryId].gradient;
  }
  const idx = hash(id) % ACCENT_PAIRS.length;
  const [a, b] = ACCENT_PAIRS[idx]!;
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}

export function formatEventDate(iso: string): {
  weekday: string;
  monthDay: string;
  time: string;
  full: string;
} {
  const d = new Date(iso);
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
    monthDay: d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
    time: d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }),
    full: d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
  };
}
